/**
 * CheckoutReturnPage — landing after a payment attempt.
 *
 * Route: `/checkout/return` (ProtectedRoute).
 *
 * Two ways to arrive:
 *   - MOCK mode: CheckoutPage's simulated gateway navigates here with
 *     router state { status, transaction, proforma }. We render directly
 *     from that — no fetch needed.
 *   - REAL mode: the payment gateway (via the backend's configured return
 *     URL) redirects the browser here with query params, e.g.
 *     `?orderId=...&status=success`. The backend has already recorded the
 *     outcome from the gateway webhook, so we fetch the transaction by id
 *     to show authoritative details.
 *
 * Success → confirmation + tracking number + "the clinic has been
 * notified" (the provider PWA picks the payment up). Failure → error +
 * a retry that returns to the service's checkout page.
 */

import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import Container from '@/components/PageSheet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import { transactionsApi, consultationsApi } from '@/services/api';
import { selectLanguage } from '@/store/uiSlice';
import { formatToman } from '@/utils/currency';
import { localized } from '@/utils/localizedContent';

export function CheckoutReturnPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // RULE 4 — a paid service that includes physician confirmation lands the
  // client on "choose your doctor". Nothing downstream happens until they do,
  // so we surface it here rather than leaving it on the dashboard only.
  const { data: pendingConsultation } = useQuery({
    queryKey: ['pending-consultation'],
    queryFn: async () => {
      const list = await consultationsApi.listConsultations();
      return list.find((c) => c.status === 'awaiting_physician') || null;
    },
    enabled: true,
  });
  const location = useLocation();
  const language = useSelector(selectLanguage);
  const [searchParams] = useSearchParams();

  // Mock path: everything arrives in router state.
  const stateOutcome = location.state;

  // Real path: gateway redirect carries orderId + status in the URL.
  const orderId = searchParams.get('orderId');
  const urlStatus = searchParams.get('status');

  // Fetch the transaction only on the real path (orderId present, no
  // router state). Disabled otherwise so mock mode does zero network.
  const {
    data: fetchedTxn,
    isLoading: fetching,
    isError: fetchError,
  } = useQuery({
    queryKey: ['transaction', orderId],
    queryFn: () => transactionsApi.getTransaction(orderId),
    enabled: !stateOutcome && !!orderId,
  });

  // Resolve the outcome from whichever source we have.
  const transaction = stateOutcome?.transaction ?? fetchedTxn ?? null;
  const status = stateOutcome?.status ?? urlStatus ?? transaction?.status ?? null;
  const amount =
    stateOutcome?.proforma?.totalAmount ?? transaction?.amount ?? null;

  // ---- No outcome at all (someone hit /checkout/return directly) ----
  if (!stateOutcome && !orderId) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="info">
          {t('checkout.return.noOutcome')}
          <Box sx={{ mt: 1.5 }}>
            <Button size="small" onClick={() => navigate('/services')}>
              {t('services.detail.notFound.backToList')}
            </Button>
          </Box>
        </Alert>
      </Container>
    );
  }

  // ---- Real path still fetching ----
  if (fetching) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography color="text.secondary">
            {t('checkout.return.verifying')}
          </Typography>
        </Stack>
      </Container>
    );
  }

  const isSuccess = status === 'success' && !fetchError;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 6 } }}>
      <Stack spacing={3} alignItems="center">
        {isSuccess ? (
          <CheckCircleIcon color="success" sx={{ fontSize: 72 }} />
        ) : (
          <CancelIcon color="error" sx={{ fontSize: 72 }} />
        )}

        <Typography variant="h5" fontWeight={700} textAlign="center">
          {isSuccess
            ? t('checkout.return.successTitle')
            : t('checkout.return.failureTitle')}
        </Typography>

        <Typography variant="body2" color="text.secondary" textAlign="center">
          {isSuccess
            ? t('checkout.return.successMessage')
            : t('checkout.return.failureMessage')}
        </Typography>

        {/* Receipt card — success only */}
        {isSuccess && transaction && (
          <Card variant="outlined" sx={{ width: '100%' }}>
            <CardContent>
              <Stack spacing={1}>
                {(() => {
                  const serviceName = localized(transaction.serviceName, language);
                  return serviceName ? (
                    <ReceiptRow
                      label={t('checkout.return.service')}
                      value={serviceName}
                    />
                  ) : null;
                })()}
                {transaction.clinicName && (
                  <ReceiptRow
                    label={t('checkout.return.clinic')}
                    value={transaction.clinicName}
                  />
                )}
                {amount != null && (
                  <ReceiptRow
                    label={t('checkout.return.amountPaid')}
                    value={formatToman(amount, language)}
                  />
                )}
                {transaction.gateway && (
                  <ReceiptRow
                    label={t('checkout.return.gateway')}
                    value={transaction.gateway}
                  />
                )}
                {transaction.trackingNumber && (
                  <ReceiptRow
                    label={t('checkout.return.trackingNumber')}
                    value={transaction.trackingNumber}
                  />
                )}
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="caption" color="text.secondary">
                  {t('checkout.return.clinicNotified')}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <Stack spacing={1.5} sx={{ width: '100%' }}>
          {!isSuccess && transaction?.serviceId && (
            <Button
              fullWidth
              variant="contained"
              onClick={() =>
                navigate(`/services/${transaction.serviceId}/checkout`, {
                  replace: true,
                })
              }
            >
              {t('checkout.return.tryAgain')}
            </Button>
          )}
          {isSuccess && pendingConsultation && (
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate(
                `/consultations/${pendingConsultation.id}/choose-physician`, { replace: true })}
            >
              {t('pickPhysician.title')}
            </Button>
          )}
          <Button
            fullWidth
            variant={isSuccess && !pendingConsultation ? 'contained' : 'outlined'}
            onClick={() => navigate('/services', { replace: true })}
          >
            {t('checkout.return.backToServices')}
          </Button>
          <Button
            fullWidth
            variant="text"
            onClick={() => navigate('/dashboard', { replace: true })}
          >
            {t('checkout.return.backToDashboard')}
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}

function ReceiptRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'end' }}>
        {value}
      </Typography>
    </Stack>
  );
}

export default CheckoutReturnPage;
